import type { ItemFieldRow } from './item-field-reader';
import { readLayerScoringToken } from './layer-cell-readers';
import { isPhpNumeric, phpJsonFloat } from './php-decimal-values';
import { phpFloatToInteger } from './php-integer-arithmetic';
import { phpJsonString, phpJsonTruthy } from './php-json-values';
import { readItemField } from './item-field-reader';

/** The native template title uses saved member names in SELECT order. */
export function enchantmentTemplateDisplayTitle(members: readonly unknown[]): string {
    return members.map(row => readItemField(row as ItemFieldRow, 'item_name')).filter(phpJsonTruthy).map(phpJsonString).join(' | ');
}

const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const has = (value: unknown, key: string): boolean => arrayLike(value) && Object.prototype.hasOwnProperty.call(value, key);
const own = (value: unknown, key: string): unknown => has(value, key) ? (value as Readonly<Record<string, unknown>>)[key] : undefined;
const validUuid = (value: unknown): value is string => typeof value === 'string'
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(value);
const trim = (value: string): string => value.replace(/^[\x00\x09\x0A\x0B\x0D\x20]+|[\x00\x09\x0A\x0B\x0D\x20]+(?![\s\S])/g, '');
function clone(value: unknown): unknown {
    if (!arrayLike(value)) return value ?? null;
    const entries = Object.entries(value);
    return entries.every(([key], index) => key === String(index)) ? entries.map(([, child]) => clone(child))
        : Object.fromEntries(entries.map(([key, child]) => [key, clone(child)]));
}
function stringArgument(value: unknown): string {
    if (value == null || arrayLike(value)) throw new TypeError('Enchantment item lookup requires a string argument');
    return phpJsonString(value);
}
export class MissingEnchantmentColumn extends Error {
    constructor(public readonly column: string) { super(`Unavailable enchantment source column: ${column}`); this.name = 'MissingEnchantmentColumn'; }
}
function column(row: unknown, key: string): unknown {
    if (!has(row, key)) throw new MissingEnchantmentColumn(key);
    return own(row, key);
}
/** Full item_arr_numeric_slot: numeric scalars pass through; structured cells use
 * the actual scoring-token parser, including its PHP string precision and trim.
 */
export function readItemNumericSlot(cell: unknown, fallback = 0): number {
    if (typeof cell === 'number') return cell;
    const token = typeof cell === 'string' ? trim(cell) : arrayLike(cell) ? readLayerScoringToken(cell) : null;
    return token !== null && isPhpNumeric(token) ? phpJsonFloat(token) : fallback;
}
/** _ft_lean_pl_walk drops a PL breakdown only when its numeric zero key exists.
 * Walk the entire resulting tree; PL maps without that key remain unchanged.
 */
export function leanNestedPl(target: ItemFieldRow): ItemFieldRow {
    if (!arrayLike(target)) throw new TypeError('Nested PL cleanup requires an array');
    const out = Object.fromEntries(Object.entries(target));
    const computed = own(out, 'computed'), pl = own(computed, 'pl');
    if (arrayLike(pl) && has(pl, '0')) out.computed = { ...computed as Record<string, unknown>, pl: own(pl, '0') };
    for (const [key, value] of Object.entries(out)) if (arrayLike(value)) out[key] = leanNestedPl(value);
    // Children have already been copied. Canonicalize this level without cloning
    // every descendant again at each ancestor in a large full-nested item tree.
    const entries = Object.entries(out);
    return entries.every(([key], index) => key === String(index)) ? entries.map(([, value]) => value) : out;
}
export function mergeEnchantmentArrays(left: ItemFieldRow, right: ItemFieldRow): ItemFieldRow {
    const entries = new Map<string, unknown>(); let next = 0;
    for (const [key, value] of [...Object.entries(left), ...Object.entries(right)]) {
        const integerKey = /^(?:0|-?[1-9]\d*)$/.test(key) && BigInt(key) >= -9223372036854775808n && BigInt(key) <= 9223372036854775807n;
        entries.set(integerKey ? String(next++) : key, value);
    }
    return clone(Object.fromEntries(entries)) as ItemFieldRow;
}
export interface EnchantmentTemplateReaders {
    templateRows: (id: string) => ItemFieldRow;
    memberRows: (uppercaseId: string) => ItemFieldRow;
    fullItem: (id: string) => ItemFieldRow | null;
    layerValue: (row: ItemFieldRow | null, name: string) => unknown;
    layerEntries: (row: ItemFieldRow | null, name: string) => ItemFieldRow;
    hasLayer: (row: ItemFieldRow | null, name: string) => boolean;
    dynamicLayers: readonly string[];
    spellCosts: readonly number[];
    /** Diagnostic event; it does not reproduce display_warning's rendered text. */
    unresolvedMember?: () => void;
}
export interface EnchantmentTemplateMemoEntry { tpl: ItemFieldRow; ench: ItemFieldRow }
/** Request-owned query memo only. No item/instance compile output is cached here.
 * Unknown query inputs must throw; a known empty template result returns null.
 */
export function createEnchantmentTemplateBuilder(readers: EnchantmentTemplateReaders,
    memoEnabled = false, initialMemo: Readonly<Record<string, EnchantmentTemplateMemoEntry | null>> = {}):
    (id: unknown, includePassiveLayers?: unknown) => ItemFieldRow | null {
    const memo = new Map(Object.entries(initialMemo).map(([key, value]) => [key, clone(value) as EnchantmentTemplateMemoEntry | null]));
    return (id, includePassiveLayers = true) => {
        if (!phpJsonTruthy(id) || !validUuid(id)) return null;
        const memoKey = id.toUpperCase(), hit = memo.get(memoKey);
        const templates = hit != null ? hit.tpl : readers.templateRows(id);
        if (!phpJsonTruthy(templates)) return null;
        const template = column(templates, '0');
        const members = hit != null ? hit.ench : readers.memberRows(memoKey);
        if (hit == null && memoEnabled) memo.set(memoKey, clone({ tpl: templates, ench: members }) as unknown as EnchantmentTemplateMemoEntry);
        const grouped: Record<string, unknown[]> = Object.create(null), sourceRows: unknown[] = [];
        const name = enchantmentTemplateDisplayTitle(Object.values(members));
        for (const member of Object.values(members)) {
            const type = phpJsonString(column(member, 'enchantment_type')), itemId = column(member, 'enchantment_item_id');
            (grouped[type] ??= []).push(itemId);
            const itemName = column(member, 'item_name');
            sourceRows.push({ enchantment_type: column(member, 'enchantment_type'), enchantment_item_id: itemId, item_name: itemName });
        }
        const layers: Record<string, ItemFieldRow> = Object.create(null), nested: Record<string, unknown[]> = Object.create(null);
        let totalCost = 0, itemCharges: unknown = null, rechargeable = 0;
        const costMultipliers: number[] = [], weightMultipliers: number[] = [];
        const addNested = (type: string, item: ItemFieldRow): void => {
            (nested[type] ??= []).push({ item_arr: item, source: id, built_from: 'enchantment' });
        };
        const imbued = ['innate_spell_per_long_rest', 'at_will_spell', 'charged_spell', 'charged_ability'];
        for (const [index, type] of imbued.entries()) {
            if (!has(grouped, type)) continue;
            let subtotal = 0;
            for (const itemId of grouped[type]!) {
                const item = readers.fullItem(stringArgument(itemId));
                const cost = readers.hasLayer(item, 'spell_level')
                    ? readers.spellCosts[Number(phpFloatToInteger(readItemNumericSlot(readers.layerValue(item, 'spell_level') ?? 0)))] ?? 0
                    : readItemNumericSlot(readers.layerValue(item, 'cost_gp') ?? 0);
                subtotal += cost;
                const previous = layers[type] ?? []; layers[type] = mergeEnchantmentArrays(previous, [itemId]);
                // Actual PHP does not use the passive/active missing-member skip for spells.
                addNested(type, leanNestedPl(item as ItemFieldRow));
            }
            const multiple = readers.hasLayer(grouped, 'item_rechargeable') && (type === 'charged_spell' || type === 'charged_ability')
                ? 1 : [3, 5, 7, 10][index]!;
            totalCost += subtotal * multiple;
        }
        if (readers.hasLayer(grouped, 'item_charges') && readers.hasLayer(grouped, 'item_rechargeable')) {
            totalCost += readItemNumericSlot(readers.layerValue(grouped, 'item_charges') ?? 0) * 50;
            itemCharges = readItemNumericSlot(readers.layerValue(grouped, 'item_charges') ?? 0); rechargeable = 1;
        } else if (readers.hasLayer(grouped, 'item_charges')) itemCharges = readItemNumericSlot(readers.layerValue(grouped, 'item_charges') ?? 0);
        if (readers.hasLayer(grouped, 'item_rechargeable')) rechargeable = 1;
        if (readers.hasLayer(grouped, 'passive_ability')) {
            layers.passive_ability = grouped.passive_ability!;
            for (const itemId of Object.values(readers.layerEntries(grouped, 'passive_ability'))) {
                const item = readers.fullItem(stringArgument(itemId));
                if (!arrayLike(item) || !Object.keys(item).length) { readers.unresolvedMember?.(); continue; }
                const lean = leanNestedPl(item); addNested('passive_ability', lean);
                if (readers.hasLayer(lean, 'cost_gp')) totalCost += readItemNumericSlot(readers.layerValue(lean, 'cost_gp') ?? 0);
                if (readers.hasLayer(lean, 'cost_gp_multiplier')) costMultipliers.push(readItemNumericSlot(readers.layerValue(lean, 'cost_gp_multiplier') ?? 0, 1));
                if (readers.hasLayer(lean, 'weight_multiplier')) weightMultipliers.push(readItemNumericSlot(readers.layerValue(lean, 'weight_multiplier') ?? 0, 1));
                if (phpJsonTruthy(includePassiveLayers) && !readers.hasLayer(lean, 'requires_equipped_item')) {
                    for (const type of readers.dynamicLayers) {
                        const entries = readers.layerEntries(lean, type);
                        if (phpJsonTruthy(entries)) layers[type] = mergeEnchantmentArrays(layers[type] ?? [], entries);
                    }
                }
            }
        }
        for (const type of ['attack_ability', 'opportunity_attack_ability', 'trap_ability']) {
            if (!readers.hasLayer(grouped, type)) continue;
            layers[type] = grouped[type]!;
            for (const itemId of Object.values(readers.layerEntries(grouped, type))) {
                const item = readers.fullItem(stringArgument(itemId));
                if (!arrayLike(item) || !Object.keys(item).length) { readers.unresolvedMember?.(); continue; }
                const lean = leanNestedPl(item); addNested(type, lean);
                if (readers.hasLayer(lean, 'cost_gp')) totalCost += readItemNumericSlot(readers.layerValue(lean, 'cost_gp') ?? 0);
            }
        }
        return clone({ enchantment_template_id: [id], item_name: [name], item_type: ['enchantment_template'],
            item_charges: [itemCharges], item_rechargeable: [rechargeable], total_cost: [totalCost],
            cost_multipliers: costMultipliers, weight_multipliers: weightMultipliers, enchantment_templates: template,
            enchantment_template_enchantments: sourceRows, layers_to_add: layers, nested_abilities: nested }) as ItemFieldRow;
    };
}
